import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Lock, Eye, Trash2, Building2, ExternalLink } from 'lucide-react';

/**
 * Plaid Consent Modal (B2)
 * Mandatory pre-Link consent screen. Plaid reviewers require explicit,
 * granular consent separate from generic ToS acceptance before Link opens.
 */
export default function PlaidConsentModal({ open, onAccept, onDecline, loading = false }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onDecline?.(); }}>
      <DialogContent className="max-w-lg" data-testid="plaid-consent-modal">
        <DialogHeader>
          <div className="flex items-center justify-center mb-2">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-emerald-600" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl">
            Before we connect your bank
          </DialogTitle>
          <DialogDescription className="text-center">
            We use <strong>Plaid</strong>, a regulated financial-data partner, to
            securely link your account. Here's exactly what that means:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-3 text-sm">
          <ConsentRow
            Icon={Building2}
            color="text-indigo-600"
            title="What we access"
            body="Your account & routing numbers, balances, the last 24 months of transactions, and account-holder name."
          />
          <ConsentRow
            Icon={Eye}
            color="text-amber-600"
            title="Why we need it"
            body="To verify your identity and confirm your monthly Susu contributions are from a real account you own."
          />
          <ConsentRow
            Icon={Lock}
            color="text-emerald-600"
            title="How it's protected"
            body="Your bank credentials are NEVER sent to us. Plaid encrypts them end-to-end; we store an encrypted token at rest (AES-256)."
          />
          <ConsentRow
            Icon={Trash2}
            color="text-rose-600"
            title="Your rights"
            body="You can disconnect this account at any time, and you can permanently delete all of your data from Settings → Delete my data."
          />
        </div>

        <div className="text-xs text-stone-500 bg-stone-50 rounded-lg p-3 leading-relaxed">
          By continuing, you authorize us and Plaid to access and share your
          financial account information as described above. You've reviewed our{' '}
          <a href="/privacy" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center">
            Privacy Policy <ExternalLink className="w-3 h-3 ml-0.5" />
          </a>{' '}and{' '}
          <a href="/terms" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center">
            Terms <ExternalLink className="w-3 h-3 ml-0.5" />
          </a>{', and '}
          <a href="https://plaid.com/legal/" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center">
            Plaid's privacy notice <ExternalLink className="w-3 h-3 ml-0.5" />
          </a>.
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onDecline}
            disabled={loading}
            data-testid="plaid-consent-decline"
            className="w-full sm:w-auto"
          >
            Not now
          </Button>
          <Button
            onClick={onAccept}
            disabled={loading}
            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
            data-testid="plaid-consent-accept"
          >
            I understand — connect my bank
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsentRow({ Icon, color, title, body }) {
  return (
    <div className="flex items-start gap-3 bg-white border border-stone-200 rounded-lg p-3">
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${color}`} />
      <div>
        <div className="font-semibold text-stone-900 text-sm">{title}</div>
        <div className="text-stone-600 text-xs mt-0.5 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
